import { useState, useEffect, useCallback } from 'react';
import { appApi, ErroApp } from './api';
import * as push from './push';

// O estado dos avisos de mensagem, num lugar so (DA-532 / DA-534).
//
// ⚠️ **Vive num hook porque agora tem DOIS donos**: a faixa que convida quem ainda
// nao ligou, e a tela de Configuracoes dentro do menu, que liga e desliga. Se cada
// um apurasse por conta propria, os dois registrariam o worker e os dois
// inscreveriam o aparelho na abertura — trabalho dobrado num caminho que ja e'
// delicado (a Apple e' exigente com QUANDO a permissao e' pedida).
//
// Quem chama e' o `ChatApp`, uma vez, e passa pra baixo.

export type SituacaoAviso =
  /** Ainda perguntando ao aparelho e ao servidor. Nao desenhar nada. */
  | 'apurando'
  /** Da' pra ligar e nao esta ligado. */
  | 'oferecer'
  /** iPhone em aba do Safari: falta por na tela de inicio antes. */
  | 'ensinar-iphone'
  /** A pessoa ja recusou no navegador; so as configuracoes do site liberam. */
  | 'bloqueado'
  /** Ligado neste aparelho. */
  | 'ligado'
  /** Nao ha caminho aqui (servidor sem chave, navegador sem suporte). */
  | 'indisponivel';

export interface Avisos {
  situacao: SituacaoAviso;
  ocupado: boolean;
  erro: string | null;
  /**
   * A medicao crua do ultimo pedido de permissao (o que o navegador respondeu, e em
   * quanto tempo). So aparece quando algo falhou, em letra miuda — e' pra eu poder
   * ler pelo print do cliente, nao pro cliente entender.
   */
  detalhe: string | null;
  ligar: () => Promise<void>;
  desligar: () => Promise<void>;
}

export function useAvisos(): Avisos {
  const [situacao, setSituacao] = useState<SituacaoAviso>('apurando');
  const [chave, setChave] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [detalhe, setDetalhe] = useState<string | null>(null);

  const apurar = useCallback(async () => {
    // O worker e' registrado AQUI, na abertura — nao no clique. A Apple manda
    // chamar a inscricao "immediately from the gesture's event handler", e esperar
    // o registro dentro do clique gastaria a interacao do usuario.
    await push.prepararWorker();

    let estado;
    try {
      estado = await appApi.pushEstado();
    } catch {
      setSituacao('indisponivel');
      return;
    }
    setChave(estado.chave_publica);

    // Servidor sem chave: nao ha o que oferecer, e um botao que sempre falha e'
    // pior que botao nenhum.
    if (!estado.disponivel) { setSituacao('indisponivel'); return; }

    if (push.suportado()) {
      // ⚠️ **Quem ja tem permissao NUNCA e' perguntado de novo.** Ate 14/08/2026
      // este caminho chamava `ativar`, que pedia a permissao antes de inscrever —
      // e pedir permissao ja concedida nao e' inofensivo: a Apple exige que o
      // pedido saia de um gesto, entao na abertura da tela ele podia ser recusado
      // de saida. A recusa nao diz "ja esta ligado", diz "nao pode agora", e o
      // resultado era o convite **voltando a cada abertura**, pedindo o que ja
      // estava dado. Agora inscreve direto, sem perguntar nada.
      //
      // E o estado sai do que o APARELHO ja sabe, nao de uma ida ao servidor: com
      // permissao concedida e inscricao viva aqui, esta ligado — ponto. O
      // re-cadastro no servidor vai junto, mas em segundo plano, porque uma falha
      // de rede nao pode fazer a tela dizer "desligado" pra quem esta ligado.
      if (push.permissao() === 'concedida') {
        const atual = await push.inscricaoAtual();
        if (atual) {
          setSituacao('ligado');
          appApi.pushInscrever(atual).catch(() => { /* a proxima abertura tenta */ });
          return;
        }
        // Permissao dada, mas sem inscricao neste aparelho (limpou dados do site,
        // ou o navegador reciclou). Inscreve calado — sem perguntar de novo.
        try {
          const nova = await push.inscrever(estado.chave_publica);
          await appApi.pushInscrever(nova);
          setSituacao('ligado');
          return;
        } catch {
          setSituacao('oferecer');
          return;
        }
      }

      // ⚠️ Negado NAO ganha botao. Depois de um "Nao permitir" o navegador para
      // de mostrar o pedido — chamar de novo devolve negado na hora, sem janela.
      // Um botao que so pode falhar e' beco sem saida; o que resolve e' dizer
      // ONDE desbloquear (`CLAUDE.md` raiz, § UI: botao travado diz por que).
      if (push.permissao() === 'negada') { setSituacao('bloqueado'); return; }

      setSituacao('oferecer');
      return;
    }

    if (push.precisaInstalarPrimeiro()) { setSituacao('ensinar-iphone'); return; }

    // Navegador que nao recebe push e nao tem caminho pra receber: fica quieto.
    setSituacao('indisponivel');
  }, []);

  useEffect(() => { void apurar(); }, [apurar]);

  const ligar = useCallback(async () => {
    setErro(null);
    setDetalhe(null);
    setOcupado(true);
    try {
      // `ativar` (e nao `inscrever`) porque AQUI ha o toque que a Apple exige pra
      // pedir a permissao. E' o unico lugar do app onde esse pedido pode sair.
      const inscricao = await push.ativar(chave);
      await appApi.pushInscrever(inscricao);
      setSituacao('ligado');
    } catch (e) {
      // ⚠️ **Recusa aqui TEM que virar 'bloqueado'.** A regra "negado nao ganha
      // botao" (acima, na apuracao) so cobria quem CHEGA na tela ja bloqueado —
      // faltava no caminho de saida, que e' justamente por onde a recusa nasce.
      // Sem isto a faixa continua oferecendo "Ativar avisos" com um erro vermelho
      // embaixo: um botao que, dali em diante, so pode falhar — porque o navegador
      // devolve negado na hora, sem abrir janela nenhuma. Quem recusou precisa ver
      // ONDE desbloquear, nao o mesmo botao de novo (`CLAUDE.md` raiz, § UI).
      if (push.permissao() === 'negada') setSituacao('bloqueado');
      setErro(
        e instanceof ErroApp || e instanceof push.ErroPush
          ? e.message
          : 'Nao consegui ligar os avisos neste aparelho.',
      );
      if (e instanceof push.ErroPush && e.detalhe) setDetalhe(e.detalhe);
    } finally {
      setOcupado(false);
    }
  }, [chave]);

  const desligar = useCallback(async () => {
    setErro(null);
    setOcupado(true);
    try {
      const endpoint = await push.desativar();
      if (endpoint) await appApi.pushCancelar(endpoint);
      // ⚠️ Volta pra 'oferecer', nao pra 'indisponivel': desligar nao tira o
      // recurso do aparelho, so o desliga. A permissao do navegador continua
      // concedida, entao religar depois nem pergunta de novo.
      setSituacao('oferecer');
      push.limparBadge();
    } catch {
      setErro('Nao consegui desligar os avisos.');
    } finally {
      setOcupado(false);
    }
  }, []);

  return { situacao, ocupado, erro, detalhe, ligar, desligar };
}
